/** Present only earned, canonical Steam events. One bounded notice replaces its
 * predecessor and expires on the existing active clock; no random draws, score
 * mutations, storage or independent animation/timer loop are introduced. */
export function createSteamArcade(r) {
  const root = r.root;
  const notice = root.querySelector('#arcade-reward');
  const title = root.querySelector('#arcade-reward-title');
  const detail = root.querySelector('#arcade-reward-detail');
  const fuel = root.querySelector('#fuel-status');
  const chainBar = root.querySelector('#chain-meter');
  const quickStart = root.querySelector('#quick-start');
  const aimHint = root.querySelector('#aim-hint');
  let previous = null,
    expires = 0;
  function show(heading, copy, kind) {
    title.textContent = heading;
    detail.textContent = copy;
    notice.dataset.kind = kind;
    notice.hidden = false;
    expires = r.clock.now() + 1900;
    // Replacing one finite animation avoids a class/forced-layout restart.
    if (!globalThis.window?.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (const animation of notice.getAnimations?.() ?? []) animation.cancel();
      notice.animate?.(
        [
          { opacity: 0, transform: 'translateY(14px) scale(.86)' },
          { opacity: 1, transform: 'translateY(0) scale(1.05)', offset: 0.65 },
          { opacity: 1, transform: 'translateY(0) scale(1)' },
        ],
        { duration: 380, easing: 'cubic-bezier(.2,.8,.2,1)' },
      );
    }
  }
  return {
    paint() {
      const game = r.game,
        chain = game.skillChain;
      const state = {
        run: game.currentRunId,
        clears: game.boardClears,
        deployments: game.neutronDeployments,
        ready: game.neutronFuel >= game.neutronFuelCost,
      };
      if (!previous || previous.run !== state.run) {
        notice.hidden = true;
        expires = 0;
      } else if (state.clears > previous.clears) {
        show(
          'BOARD MASTERED',
          `Clear ${state.clears} · Hit power ${r.getCurrentPlayerBallDamage()}`,
          'clear',
        );
      } else if (state.deployments > previous.deployments) {
        show(
          chain.multiplier > 1 ? `OVERDRIVE ×${chain.multiplier}` : 'NEUTRON SALVO',
          chain.multiplier > 1
            ? 'Linked specials · multiplier earned'
            : 'Fire another special within 5s to chain',
          'chain',
        );
      } else if (state.ready && !previous.ready) {
        show('FUEL READY', 'Tap the lower-left tank · N on keyboard', 'fuel');
      }
      previous = state;
      if (
        (expires && r.clock.now() >= expires) ||
        game.status === 'gameover' ||
        game.scoreboardMode === 'view'
      )
        notice.hidden = true;
      if (globalThis.window?.matchMedia('(prefers-reduced-motion: reduce)').matches)
        for (const animation of notice.getAnimations?.() ?? []) animation.cancel();
      quickStart.hidden = game.modeRunStarted || game.scoreboardMode === 'view';
      aimHint.hidden = !game.aiming || r.ui.overlay.classList.contains('is-visible');
      if (!aimHint.hidden) aimHint.textContent = `${game.angle}° · Release to launch`;
      fuel.hidden = !game.modeRunStarted || game.status === 'gameover';
      const fuelText = state.ready
        ? 'N · FUEL READY'
        : `FUEL ${game.neutronFuel}/${game.neutronFuelCost}`;
      if (fuel.textContent !== fuelText) fuel.textContent = fuelText;
      fuel.dataset.ready = String(state.ready);
      chainBar.hidden = !chain.active;
      chainBar.setAttribute('aria-label', `Special chain ${chain.multiplier} times`);
      const remaining =
        chain.multiplier > 1
          ? chain.multiplierRemainingSeconds / 8
          : chain.windowRemainingSeconds / 5;
      chainBar.style?.setProperty('--chain-remaining', String(Math.max(0, Math.min(1, remaining))));
    },
    dispose() {
      for (const animation of notice.getAnimations?.() ?? []) animation.cancel();
    },
  };
}
